import type { ComponentType } from "react";

// Brand marks for the connectors, as inline SVG rather than lucide glyphs.
//
// A generic envelope for Gmail and a generic envelope for Outlook made the two indistinguishable
// in a grid, which is the one job a logo has. These are hand-drawn simplifications in each brand's
// own colours — recognisable at 16px, not pixel-exact reproductions of the official assets. If you
// want the real files, every entry here is a drop-in replacement: swap the paths, keep the export.
//
// Colours are hardcoded because a brand mark is not themeable; the two marks that are effectively
// monochrome (GitHub, the generic ones) use currentColor so they invert with the theme instead of
// vanishing into a dark background.

export type BrandIcon = ComponentType<{ className?: string }>;

// Each mark gets a viewBox tightened to its own artwork rather than a shared 0 0 24 24. Drawn on a
// 24-unit grid, most of these sit inset — Gmail's envelope spans y 5-19, so on a shared box a 24px
// render produced a 14px glyph and the logo read as a speck inside its tile. A tight box makes the
// rendered size the real size. Update the box alongside any path change, or the padding comes back.
const BOX = {
  gmail: "3 5 18 14",
  outlook: "2 4 19 16",
  teams: "1.6 4 21 15.4",
  smtp: "3 4 18 16",
  stripe: "2 2 20 20",
  github: "2 2 20 20",
  figma: "5 1 14 21",
  joyloSso: "4 1.8 16 20.2",
  universalKey: "3.6 3.6 17.6 16.4",
} as const;

export const GmailMark: BrandIcon = ({ className }) => (
  <svg className={className} viewBox={BOX.gmail} fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M3 6.5A1.5 1.5 0 0 1 4.5 5h15A1.5 1.5 0 0 1 21 6.5v11a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 17.5v-11Z" fill="#fff" />
    <path d="M3 6.5A1.5 1.5 0 0 1 4.5 5H6v14H4.5A1.5 1.5 0 0 1 3 17.5v-11Z" fill="#4285F4" />
    <path d="M18 5h1.5A1.5 1.5 0 0 1 21 6.5v11a1.5 1.5 0 0 1-1.5 1.5H18V5Z" fill="#34A853" />
    <path d="M6 19V8.7l6 4.5 6-4.5V19h-2.4v-6.9L12 15.6l-3.6-3.5V19H6Z" fill="#EA4335" />
    <path d="M3 6.9V6.5A1.5 1.5 0 0 1 4.5 5h.6L12 10.2 18.9 5h.6A1.5 1.5 0 0 1 21 6.5v.4l-9 6.75L3 6.9Z" fill="#C5221F" />
  </svg>
);

export const OutlookMark: BrandIcon = ({ className }) => (
  <svg className={className} viewBox={BOX.outlook} fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M10 5h10a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H10V5Z" fill="#0F6CBD" />
    <path d="M10 7.2h11v2.1l-5.5 3.4L10 9.3V7.2Z" fill="#fff" fillOpacity=".9" />
    <path d="M21 9.6V18a1 1 0 0 1-1 1H10v-8.2l5.5 3.3L21 9.6Z" fill="#1A8CFF" fillOpacity=".55" />
    <rect x="2" y="4" width="9.5" height="16" rx="1.6" fill="#0A4C8A" />
    <ellipse cx="6.75" cy="12" rx="2.9" ry="3.6" fill="#fff" />
    <ellipse cx="6.75" cy="12" rx="1.35" ry="1.9" fill="#0A4C8A" />
  </svg>
);

export const TeamsMark: BrandIcon = ({ className }) => (
  <svg className={className} viewBox={BOX.teams} fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="17.2" cy="6.6" r="2.6" fill="#5B5FC7" />
    <path d="M14.4 10.4H22a.6.6 0 0 1 .6.6v4.2a3.6 3.6 0 0 1-3.6 3.6h-.4a4.2 4.2 0 0 1-4.2-4.2v-4.2Z" fill="#7B83EB" />
    <rect x="1.6" y="4.6" width="13.2" height="14.8" rx="1.8" fill="#4B53BC" />
    <path d="M5 8.6h6.4v1.7H9.1V16H7.3v-5.7H5V8.6Z" fill="#fff" />
  </svg>
);

export const SmtpMark: BrandIcon = ({ className }) => (
  <svg className={className} viewBox={BOX.smtp} fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <rect x="3" y="4" width="18" height="6" rx="1.6" fill="#475569" />
    <rect x="3" y="14" width="18" height="6" rx="1.6" fill="#64748B" />
    <circle cx="6.6" cy="7" r="1" fill="#4ADE80" />
    <circle cx="6.6" cy="17" r="1" fill="#4ADE80" />
    <path d="M11 5.9h7.2v2.2L14.6 10 11 8.1V5.9Z" fill="#fff" fillOpacity=".85" />
  </svg>
);

export const StripeMark: BrandIcon = ({ className }) => (
  <svg className={className} viewBox={BOX.stripe} fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <rect x="2" y="2" width="20" height="20" rx="4.5" fill="#635BFF" />
    <path d="M11.4 9.6c0-.55.47-.78 1.2-.78.98 0 2.2.3 3.18.83V7.05a8.2 8.2 0 0 0-3.18-.6c-2.6 0-4.33 1.37-4.33 3.65 0 3.56 4.85 2.98 4.85 4.52 0 .65-.56.86-1.33.86-1.05 0-2.44-.44-3.5-1.03v2.47c1.13.49 2.3.7 3.5.7 2.67 0 4.5-1.32 4.5-3.63 0-3.84-4.89-3.15-4.89-4.4Z" fill="#fff" />
  </svg>
);

export const GithubMark: BrandIcon = ({ className }) => (
  <svg className={className} viewBox={BOX.github} fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M12 2C6.48 2 2 6.58 2 12.23c0 4.52 2.87 8.35 6.84 9.7.5.09.68-.22.68-.49v-1.7c-2.78.62-3.37-1.37-3.37-1.37-.45-1.18-1.11-1.5-1.11-1.5-.91-.63.07-.62.07-.62 1 .07 1.53 1.06 1.53 1.06.89 1.57 2.34 1.12 2.91.85.09-.66.35-1.11.63-1.37-2.22-.26-4.56-1.14-4.56-5.06 0-1.12.39-2.03 1.03-2.75-.1-.26-.45-1.3.1-2.71 0 0 .84-.28 2.75 1.05a10.3 10.3 0 0 1 5.01 0c1.9-1.33 2.74-1.05 2.74-1.05.55 1.41.2 2.45.1 2.71.64.72 1.03 1.63 1.03 2.75 0 3.93-2.35 4.8-4.58 5.05.36.32.68.94.68 1.9v2.82c0 .27.18.59.69.49A10.06 10.06 0 0 0 22 12.23C22 6.58 17.52 2 12 2Z" />
  </svg>
);

export const FigmaMark: BrandIcon = ({ className }) => (
  <svg className={className} viewBox={BOX.figma} fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M8.5 22a3.5 3.5 0 0 0 3.5-3.5V15H8.5a3.5 3.5 0 0 0 0 7Z" fill="#0ACF83" />
    <path d="M5 11.5A3.5 3.5 0 0 1 8.5 8H12v7H8.5A3.5 3.5 0 0 1 5 11.5Z" fill="#A259FF" />
    <path d="M5 4.5A3.5 3.5 0 0 1 8.5 1H12v7H8.5A3.5 3.5 0 0 1 5 4.5Z" fill="#F24E1E" />
    <path d="M12 1h3.5a3.5 3.5 0 0 1 0 7H12V1Z" fill="#FF7262" />
    <path d="M19 11.5A3.5 3.5 0 0 1 15.5 15 3.5 3.5 0 0 1 15.5 8 3.5 3.5 0 0 1 19 11.5Z" fill="#1ABCFE" />
  </svg>
);

/** Joylo's own managed sign-in — not a third-party brand, so it follows the theme. */
export const JoyloSsoMark: BrandIcon = ({ className }) => (
  <svg className={className} viewBox={BOX.joyloSso} fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M12 2.6 4.8 5.4v5.9c0 4.4 2.9 8.5 7.2 9.9 4.3-1.4 7.2-5.5 7.2-9.9V5.4L12 2.6Z" fill="currentColor" fillOpacity=".14" stroke="currentColor" strokeWidth="1.5" />
    <path d="m8.9 12.1 2.2 2.2 4-4.3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const UniversalKeyMark: BrandIcon = ({ className }) => (
  <svg className={className} viewBox={BOX.universalKey} fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="8.6" cy="8.6" r="4.1" stroke="currentColor" strokeWidth="1.7" />
    <path d="m11.5 11.5 7.6 7.6M16 16l1.9-1.9M18.4 18.4l1.9-1.9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);
